import { useCallback, useEffect, useRef, useState } from 'react'
import { BOX_BREATHING_PHASES } from '../constants/wellbeingMessages'
import { getPhaseAt, getPhaseScale, nextPhaseIndex } from '../utils/breathingEngine'

/**
 * Continuous box-breathing timer. User controls stop.
 * Uses rAF-aligned setInterval for countdown; CSS handles ring motion.
 */
export function useBreathingTimer({ active = false, reduceMotion = false } = {}) {
	const [phaseIndex, setPhaseIndex] = useState(0)
	const [secondsLeft, setSecondsLeft] = useState(BOX_BREATHING_PHASES[0].seconds)
	const [running, setRunning] = useState(false)
	const intervalRef = useRef(null)

	const phase = getPhaseAt(phaseIndex)
	const scale = reduceMotion ? 1 : getPhaseScale(phase.id)

	const clear = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = null
		}
	}, [])

	const reset = useCallback(() => {
		clear()
		setPhaseIndex(0)
		setSecondsLeft(BOX_BREATHING_PHASES[0].seconds)
		setRunning(false)
	}, [clear])

	const start = useCallback(() => {
		setPhaseIndex(0)
		setSecondsLeft(BOX_BREATHING_PHASES[0].seconds)
		setRunning(true)
	}, [])

	const stop = useCallback(() => {
		clear()
		setRunning(false)
	}, [clear])

	useEffect(() => {
		if (!active) {
			reset()
			return undefined
		}
		return undefined
	}, [active, reset])

	useEffect(() => {
		if (!running || !active) {
			clear()
			return undefined
		}

		intervalRef.current = setInterval(() => {
			setSecondsLeft((prev) => {
				if (prev > 1) return prev - 1
				setPhaseIndex((idx) => {
					const next = nextPhaseIndex(idx)
					setSecondsLeft(getPhaseAt(next).seconds)
					return next
				})
				return 0
			})
		}, 1000)

		return clear
	}, [running, active, clear])

	return {
		phase,
		phaseIndex,
		secondsLeft,
		scale,
		running,
		start,
		stop,
		reset,
	}
}
